/** A run of text with the Slack marks it carries. */
export type Inline = {text: string; bold?: true; italic?: true; strike?: true; code?: true};
export type MrkdwnBlock =
    | {kind: 'line' | 'quote'; inlines: Inline[]}
    | {kind: 'code'; text: string};

type Style = Omit<Inline, 'text'>;

// Slack only opens a mark at a word edge and closes it before one, so `invoice_date_bug` stays plain.
const MARK =
    /`([^`\n]+)`|(?<![\w*])\*(?=\S)([^*\n]*?\S)\*(?![\w*])|(?<![\w_])_(?=\S)([^_\n]*?\S)_(?![\w_])|(?<![\w~])~(?=\S)([^~\n]*?\S)~(?![\w~])/g;

export const parseInline = (text: string, style: Style = {}): Inline[] => {
    const out: Inline[] = [];
    let last = 0;

    for (const match of text.matchAll(MARK)) {
        const [whole, code, bold, italic, strike] = match;
        if (match.index > last) out.push({text: text.slice(last, match.index), ...style});
        if (code !== undefined) out.push({text: code, ...style, code: true});
        else if (bold !== undefined) out.push(...parseInline(bold, {...style, bold: true}));
        else if (italic !== undefined) out.push(...parseInline(italic, {...style, italic: true}));
        else if (strike !== undefined) out.push(...parseInline(strike, {...style, strike: true}));
        last = match.index + whole.length;
    }
    if (last < text.length) out.push({text: text.slice(last), ...style});

    return out;
};

/** Slack's mrkdwn as lines, quotes and code blocks, each line's marks parsed. */
export const parseMrkdwn = (text: string): MrkdwnBlock[] => {
    const lines = text.split('\n');
    const blocks: MrkdwnBlock[] = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (line.startsWith('```')) {
            const body = [line.slice(3)];
            // A fence closes on the line that ends with ```, which may be this one.
            while (!body[body.length - 1].endsWith('```') && i + 1 < lines.length) {
                body.push(lines[++i]);
            }
            const joined = body.join('\n');
            const closed = joined.endsWith('```') ? joined.slice(0, -3) : joined;
            blocks.push({kind: 'code', text: closed.replace(/^\n|\n$/g, '')});
            continue;
        }

        const quote = /^>\s?/.exec(line);
        blocks.push(
            quote
                ? {kind: 'quote', inlines: parseInline(line.slice(quote[0].length))}
                : {kind: 'line', inlines: parseInline(line)},
        );
    }

    return blocks;
};
